import {
  debounce,
  Dialog,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  styled,
} from "@mui/material";
import { GrayButton } from "Components/Button";
import { Checkbox } from "Components/Checkbox";
import { Input } from "Components/Input";
import AIGenerateInput from "Components/input/AIGenerateInput";
import EmailInput from "Components/input/EmailInput";
import Menu from "Components/Menu";
import Row from "Components/Row";
import { OpenTabLink, Text } from "Components/Text";
import { COLORS } from "constants/layout";
import { PATHS } from "constants/routes";
import { VALID_EMAIL } from "constants/validate";
import { formatNumber } from "helpers/string";
import useUser from "hooks/useUser";
import { useCommonData } from "providers/common-data";
import { useLoading } from "providers/loading";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { apiSearchProductCategory } from "services/api/helpers";
import { apiCreateRFQ, apiGenerateRfqDetail } from "services/api/rfq";
import { ParamsType, QuickRFQFormType } from "types/params";
import QuickRFQFormSuccessful from "./QuickRFQFormSuccessful";
import { useParams } from "react-router-dom";
import { OptionType } from "types";
import { StyledAutocomplete } from "Components/input/Autocomplete.styles";
import JoinUsForm from "./JoinUsForm";
import Select from "Components/input/Select";

type Props = {
  categoryLabel?: string;
  search?: string;
  total?: number;
};

const QuickRFQForm = ({ categoryLabel, search, total = 0 }: Props) => {
  const t = useIntl();
  const { appConfigs } = useCommonData();
  const { uoms = [] } = appConfigs || {};
  const { isLoggedIn, user } = useUser();
  const { setLoading } = useLoading();
  const { categoryId } = useParams<ParamsType>();

  const [agreePolicy, setAgreePolicy] = useState(true);
  const [searchCategories, setsearchCategories] = useState([]);
  const [searchCategoryLoading, setsearchCategoryLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [activateKey, setActivateKey] = useState<string | undefined>();
  const [rfqId, setRfqId] = useState<string>();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    getValues,
    control,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<QuickRFQFormType>({
    defaultValues: {
      customer_name: "",
      country: "",
      email: "",
      phone_number: "",
      product_category_id: categoryId,
      quantity: "",
      product_category: undefined,
      details: "",
      unit: "Piece",
    },
    mode: "onChange",
  });

  const unit = watch("unit");
  const productCategory = watch("product_category");
  const phoneCountryCode = watch("country") || "";

  const countries = useMemo(
    () => appConfigs?.countries || [],
    [appConfigs?.countries]
  );

  const onClickGenerate = () => {
    if (!productCategory) {
      setError("product_category", {
        type: "manual",
        message: t.formatMessage({ id: "requied_input" }),
      });
    } else {
      clearErrors("product_category");
    }
  };

  const delayedFetchOptions = debounce((searchText: string | null) => {
    getSearchCategories(searchText);
  }, 500);

  const getSearchCategories = (search: string | null) => {
    setsearchCategoryLoading(true);
    apiSearchProductCategory(search)
      .then((res) => {
        setsearchCategories(
          (res.data?.message?.data || []).map((c: any) => ({
            value: c.name,
            label: c.category_name,
          }))
        );
      })
      .finally(() => {
        setsearchCategoryLoading(false);
      });
  };

  const onSubmit = async (data: QuickRFQFormType) => {
    try {
      let { product_category, ...rest } = data;
      setLoading(true);
      const res = await apiCreateRFQ({
        ...rest,
        product_name: product_category?.label,
      });
      onOpenDialog();
      const rfqDataRes = res.data?.message?.data;
      const rfqId = rfqDataRes?.name;
      const key = rfqDataRes?.key;
      setRfqId(rfqId);
      if (!isLoggedIn && key) {
        setActivateKey(key);
      } else {
        onResetForm();
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const onResetForm = () => {
    reset({
      ...getValues(),
      product_category: undefined,
      product_category_id: "",
      details: "",
      quantity: "",
      unit: "Piece",
    });
  };

  const onCloseDialog = () => {
    setShowDialog(false);
    onResetForm();
  };

  const onOpenDialog = () => setShowDialog(true);

  useEffect(() => {
    if (isLoggedIn) {
      const {
        fd_country = "",
        customer_name = "",
        email,
        customer_phone,
      } = user;
      reset({
        ...getValues(),
        customer_name,
        country: fd_country,
        email,
        phone_number: customer_phone,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    getSearchCategories(categoryId || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const selectedCategory = searchCategories.find(
      (category: OptionType) => category?.value === categoryId
    );
    if (categoryId && selectedCategory) {
      setValue("product_category", selectedCategory);
      setValue("product_category_id", categoryId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, searchCategories]);

  return (
    <StyledStack className="card">
      <Text className="title" mb={1}>
        {(categoryLabel || search) &&
          total > 0 &&
          t.formatMessage(
            { id: "want_to_connect_with_suppliers" },
            {
              s: (code: ReactNode) => <span>{code}</span>,
              value: formatNumber(total),
              cate: categoryLabel || search,
            }
          )}{" "}
        {t.formatMessage({ id: "let_suppliers_find_contact_you" })}
      </Text>

      <Grid
        container
        spacing={1}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Grid item xs={12} md={8} container spacing={1} height="fit-content">
          <Grid item xs={6}>
            <FormControl error={!!errors.product_category?.message} fullWidth>
              <Controller
                name="product_category"
                control={control}
                // defaultValue=""
                rules={{
                  required: t.formatMessage({ id: "requied_input" }),
                }}
                render={({ field }) => (
                  <StyledAutocomplete
                    {...field}
                    options={searchCategories}
                    value={field.value || null}
                    getOptionLabel={(option: any) => option.label || ""}
                    loading={searchCategoryLoading}
                    id="product_category_input"
                    onChange={(e, newValue: any) => {
                      setValue("product_category", newValue);
                      setValue("product_category_id", newValue?.value);
                      clearErrors("product_category");
                    }}
                    renderInput={(params) => (
                      <Input
                        {...params}
                        placeholder={t.formatMessage({
                          id: "select_product_category",
                        })}
                        fullWidth
                        onChange={(e) => delayedFetchOptions(e.target.value)}
                        error={!!errors.product_category?.message}
                      />
                    )}
                  />
                )}
              />
              {errors.product_category && (
                <FormHelperText error>
                  {errors.product_category.message}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <Input
              placeholder={t.formatMessage({ id: "EnterQuantity" })}
              {...register("quantity", {
                required: t.formatMessage({ id: "requied_input" }),
              })}
              error={!!errors.quantity?.message}
              helperText={errors.quantity?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Menu
                      value={unit}
                      buttonText={
                        uoms.find((uom: any) => uom.value === unit)?.label
                      }
                    >
                      {uoms.map(({ label, value }: any) => (
                        <MenuItem
                          key={label}
                          selected={value === unit}
                          onClick={() => setValue("unit", label)}
                        >
                          {label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            // height="75%"
            className="detail-input"
            overflow="hidden"
            sx={{ overflowY: "scroll" }}
          >
            <AIGenerateInput
              useForm={{ getValues, setValue }}
              fieldName="details"
              label={t.formatMessage({ id: "detailed_requirements" })}
              apiGenerate={() => apiGenerateRfqDetail(getValues())}
              {...{ onClickGenerate }}
              isValid={!!getValues("product_category")}
            >
              <Input
                fullWidth
                placeholder={t.formatMessage({
                  id: "details_ai_generation",
                })}
                InputProps={{
                  style: { minHeight: 50, alignItems: "flex-start" },
                }}
                multiline
                rows={4}
                maxRows={4}
                {...register("details")}
              />
            </AIGenerateInput>
          </Grid>
        </Grid>

        <Grid item xs={12} md={4} container spacing={1} height="fit-content">
          <Grid item xs={6} md={12}>
            <Input
              {...register("customer_name", {
                required: t.formatMessage({ id: "requied_input" }),
              })}
              placeholder={t.formatMessage({
                id: "enter_your_fullname",
              })}
              error={!!errors?.customer_name}
              helperText={errors?.customer_name?.message}
            />
          </Grid>

          <Grid item xs={6} md={12}>
            <EmailInput
              {...register("email", {
                required: t.formatMessage({ id: "requied_input" }),
                pattern: {
                  value: VALID_EMAIL,
                  message: t.formatMessage({ id: "invalid_email" }),
                },
              })}
              error={!!errors?.email}
              helperText={errors?.email?.message}
            />
          </Grid>

          <Grid item xs={6} md={12}>
            <FormControl
              error={!!errors?.country}
              className={phoneCountryCode ? "" : "placeholder"}
              fullWidth
            >
              <Controller
                name="country"
                control={control}
                rules={{
                  required: t.formatMessage({ id: "requied_input" }),
                }}
                defaultValue={phoneCountryCode}
                render={({ field }) => (
                  <Select {...field} options={countries} />
                )}
              />
              {errors.country && (
                <FormHelperText error>{errors.country.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6} md={12}>
            <Input
              {...register("phone_number", {
                required: t.formatMessage({ id: "requied_input" }),
              })}
              placeholder={t.formatMessage({ id: "phone_number" })}
              error={!!errors.phone_number?.message}
              helperText={errors.phone_number?.message}
            />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Row direction={{ xs: "column", md: "row" }} spacing={1}>
            <Row spacing={1} flex={1}>
              <Checkbox
                checked={agreePolicy}
                onChange={(e) => setAgreePolicy(e.target.checked)}
              />
              <Text
                fontSize={10}
                color={COLORS.gray[500]}
                className="policy-text"
              >
                {t.formatMessage(
                  { id: "confirm_policy_quick_rfq" },
                  {
                    s: (code: ReactNode) => (
                      <OpenTabLink href={PATHS.termCondition}>
                        {code}
                      </OpenTabLink>
                    ),
                    ss: (code: ReactNode) => (
                      <OpenTabLink href={PATHS.policy}>{code}</OpenTabLink>
                    ),
                    sss: (code: ReactNode) => (
                      <OpenTabLink href={PATHS.disclaimers}>{code}</OpenTabLink>
                    ),
                  }
                )}
              </Text>
            </Row>
            <GrayButton type="submit" disabled={!agreePolicy}>
              {t.formatMessage({ id: "submit" })}
            </GrayButton>
          </Row>
        </Grid>
      </Grid>

      <Dialog
        open={showDialog}
        onClose={onCloseDialog}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: !isLoggedIn && activateKey ? 678 : "initial",
          },
        }}
      >
        {!isLoggedIn && activateKey ? (
          <JoinUsForm
            {...{ activateKey }}
            onClose={onCloseDialog}
            getData={getValues}
            typeIds={{ rfqId }}
            category={productCategory}
          />
        ) : (
          <QuickRFQFormSuccessful onClose={onCloseDialog} />
        )}
      </Dialog>
    </StyledStack>
  );
};

const StyledStack = styled(Stack)(({ theme }) => ({
  ".title": {
    span: {
      color: COLORS.yellow[500],
      fontStyle: "italic",
    },
  },
  ".policy-text": {
    a: {
      color: COLORS.green[700],
    },
  },
  ".detail-input": {
    maxHeight: 168,
  },
  [theme.breakpoints.down("md")]: {
    ".title": {
      fontSize: 14,
    },
    ".detail-input": {
      height: "auto",
    },
  },
}));

export default QuickRFQForm;
